/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Checkbox } from 'antd';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { TableEntry } from '~src/utils/securityGroupsConfig';

interface CheckboxFolderTreeProps {
  folders: TableEntry[];
  selectedIds: Set<number>;
  onToggle: (folderId: number, isChecked: boolean) => void;
  expandedFolderIds: Set<number>;
  onToggleExpand: (folderId: number) => void;
}

export const CheckboxFolderTree: React.FC<CheckboxFolderTreeProps> = ({
  folders,
  selectedIds,
  onToggle,
  expandedFolderIds,
  onToggleExpand,
}) => {
  const styles = useStyles(styleFn);

  // Recursive function to get all child IDs
  const getAllChildIds = (folder: TableEntry): number[] => {
    const ids = [folder.dbFolder.id];
    if (folder.children) {
      folder.children.forEach((child) => {
        ids.push(...getAllChildIds(child));
      });
    }
    return ids;
  };

  const handleCheckboxChange = (folder: TableEntry, checked: boolean) => {
    const allIds = getAllChildIds(folder);
    allIds.forEach((id) => onToggle(id, checked));
  };

  return (
    <div css={styles.tree}>
      {folders.map((folder, index) => (
        <CheckboxFolderItem
          key={folder.key}
          folder={folder}
          level={0}
          selectedIds={selectedIds}
          onCheckboxChange={handleCheckboxChange}
          expandedFolderIds={expandedFolderIds}
          onToggleExpand={onToggleExpand}
          parentChain={[]}
          isLastChild={index === folders.length - 1}
        />
      ))}
    </div>
  );
};

interface CheckboxFolderItemProps {
  folder: TableEntry;
  level: number;
  selectedIds: Set<number>;
  onCheckboxChange: (folder: TableEntry, checked: boolean) => void;
  expandedFolderIds: Set<number>;
  onToggleExpand: (folderId: number) => void;
  parentChain: boolean[];
  isLastChild: boolean;
}

const CheckboxFolderItem: React.FC<CheckboxFolderItemProps> = ({
  folder,
  level,
  selectedIds,
  onCheckboxChange,
  expandedFolderIds,
  onToggleExpand,
  parentChain,
  isLastChild,
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();

  const isChecked = selectedIds.has(folder.dbFolder.id);
  const hasChildren = folder.children && folder.children.length > 0;
  const isExpanded = expandedFolderIds.has(folder.dbFolder.id);

  return (
    <div css={styles.itemWrapper}>
      <div css={[styles.item, { paddingLeft: level * 20 + 12 }]}>
        {/* 1. Tree lines - only for nested folders */}
        {parentChain.length > 0 && (
          <div css={styles.treeLines}>
            {/* Show empty spacers for ancestors to maintain indentation */}
            {parentChain.slice(0, -1).map((_, idx) => (
              <div key={idx} css={styles.treeLine} />
            ))}
            {/* Show connection line only for immediate parent */}
            <div
              css={[
                styles.treeLine,
                isLastChild ? styles.treeLineL : styles.treeLineT,
              ]}
            />
          </div>
        )}

        {/* 2. Checkbox - before chevron */}
        <Checkbox
          checked={isChecked}
          onChange={(e) => onCheckboxChange(folder, e.target.checked)}
          css={styles.checkbox}
        />

        {/* 3. Chevron container - always present for alignment */}
        <div css={styles.chevronContainer}>
          {hasChildren && (
            <FontAwesomeIcon
              icon={['fas', isExpanded ? 'chevron-down' : 'chevron-right']}
              css={styles.chevronIcon}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(folder.dbFolder.id);
              }}
            />
          )}
        </div>

        {/* 4. Folder icon - always aligned */}
        <FontAwesomeIcon icon={['fas', 'folder']} css={styles.folderIcon} />

        {/* 5. Folder name */}
        <span css={styles.folderName}>{folder.title}</span>
        {folder.dbFolder.description && (
          <span css={styles.folderDescription}>
            - {folder.dbFolder.description}
          </span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div css={styles.children}>
          {folder.children!.map((child, index) => (
            <CheckboxFolderItem
              key={child.key}
              folder={child}
              level={level + 1}
              selectedIds={selectedIds}
              onCheckboxChange={onCheckboxChange}
              expandedFolderIds={expandedFolderIds}
              onToggleExpand={onToggleExpand}
              parentChain={[...parentChain, isLastChild]}
              isLastChild={index === folder.children!.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  tree: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxHeight: 350,
    overflowY: 'auto',
    paddingRight: 8,
  },
  itemWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    backgroundColor: theme.colors.backgroundSecondary,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 6,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
  },
  chevronContainer: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chevronIcon: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    flexShrink: 0,
    '&:hover': {
      color: theme.colors.primary,
    },
  },
  treeLines: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    flexShrink: 0,
  },
  treeLine: {
    width: 24,
    position: 'relative',
    flexShrink: 0,
  },
  treeLineVertical: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  treeLineT: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  treeLineL: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '50%',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  checkbox: {
    flexShrink: 0,
  },
  folderIcon: {
    fontSize: 16,
    color: '#faad14 !important',
    flexShrink: 0,
  },
  folderName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
  },
  folderDescription: {
    fontSize: 13,
    color: theme.colors.textSecondary,
    fontStyle: 'italic',
    marginLeft: 4,
  },
  children: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});
